import { useMutation, useQueryClient } from "@tanstack/react-query";

import { authClient } from "@/shared/auth";
import { UserRole } from "@/features/users/type.ts";
import { USER_KEY, USERS_KEY } from "@/features/users/use-users.ts";

type UpdateUserData = Parameters<
  typeof authClient.admin.updateUser
>[0]["data"];

// better-auth не бросает исключение, а возвращает { data, error }, поэтому без
// этой проверки react-query считал успехом даже неудачное обновление.
async function updateUser(userId: string, data: UpdateUserData) {
  const result = await authClient.admin.updateUser({ userId, data });

  if (result.error) {
    throw new Error(result.error.message ?? "Не удалось обновить пользователя");
  }

  return result.data;
}

// Карточка пользователя и список читаются из кеша, поэтому после обновления их
// нужно инвалидировать — иначе поля продолжают показывать прежнее значение.
function useInvalidateUser() {
  const queryClient = useQueryClient();

  return (userId: string) => {
    queryClient.invalidateQueries({ queryKey: [USER_KEY, userId] });
    queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
  };
}

export function useUserCityUpdate({ onSuccess }: { onSuccess?: () => void }) {
  const invalidateUser = useInvalidateUser();

  return useMutation<unknown, Error, { userId: string; cityId: number }>({
    mutationFn: (data) => updateUser(data.userId, { city: data.cityId }),
    onSuccess: (_result, variables) => {
      invalidateUser(variables.userId);
      onSuccess?.();
    },
  });
}

export function useUserRoleUpdate({ onSuccess }: { onSuccess?: () => void }) {
  const invalidateUser = useInvalidateUser();

  return useMutation<unknown, Error, { userId: string; role: UserRole }>({
    mutationFn: (data) => updateUser(data.userId, { role: data.role }),
    onSuccess: (_result, variables) => {
      invalidateUser(variables.userId);
      onSuccess?.();
    },
  });
}

export function useUserNameUpdate({ onSuccess }: { onSuccess?: () => void }) {
  const invalidateUser = useInvalidateUser();

  return useMutation<unknown, Error, { userId: string; name: string }>({
    mutationFn: (data) => updateUser(data.userId, { name: data.name }),
    onSuccess: (_result, variables) => {
      invalidateUser(variables.userId);
      onSuccess?.();
    },
  });
}
